import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { agentAPI } from '../../services/api';

const STATUS = {
  idle: 'idle',
  loading: 'loading',
  ready: 'ready',
  connecting: 'connecting',
  inCall: 'inCall',
};

const STATUS_LABEL = {
  idle: 'Ready to connect',
  loading: 'Preparing voice call...',
  ready: 'Ready - click Start Call',
  connecting: 'Connecting call...',
  inCall: 'Connected to AI voice agent',
};

function loadScript(url) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => resolve(url);
    script.onerror = () => reject(new Error(`Failed to load ${url}`));
    document.head.appendChild(script);
  });
}

async function ensureTwilioSdk() {
  if (window.Twilio?.Device) return;

  const cdnList = [
    'https://unpkg.com/@twilio/voice-sdk@2.10.0/dist/twilio.min.js',
    'https://cdn.jsdelivr.net/npm/@twilio/voice-sdk@2.10.0/dist/twilio.min.js',
    'https://media.twiliocdn.com/sdk/js/client/v1.14/twilio.min.js',
  ];

  let lastErr;
  for (const cdn of cdnList) {
    try {
      await loadScript(cdn);
      if (window.Twilio?.Device) return;
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr || new Error('Unable to load voice SDK');
}

export default function VoiceCallAgent({
  displayName = '',
  prefilledEmail = '',
  onEndCall,
  onCallStart,
}) {
  const [status, setStatus] = useState(STATUS.idle);
  const [error, setError] = useState('');
  const [callSeconds, setCallSeconds] = useState(0);

  const deviceRef = useRef(null);
  const activeCallRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const inCall = status === STATUS.inCall || status === STATUS.connecting;
  const pulse = status === STATUS.loading || status === STATUS.connecting || status === STATUS.inCall;

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resetCallState = useCallback(() => {
    stopTimer();
    setCallSeconds(0);
    activeCallRef.current = null;
  }, [stopTimer]);

  const bindCallEvents = useCallback((call) => {
    call.on('ringing', () => {
      if (!mountedRef.current) return;
      setStatus(STATUS.connecting);
    });

    call.on('accept', () => {
      if (!mountedRef.current) return;
      setStatus(STATUS.inCall);
      setError('');
      setCallSeconds(0);
      stopTimer();
      timerRef.current = setInterval(() => {
        setCallSeconds((s) => s + 1);
      }, 1000);
      onCallStart?.();
    });

    call.on('disconnect', () => {
      if (!mountedRef.current) return;
      resetCallState();
      setStatus(STATUS.ready);
      onEndCall?.();
    });

    call.on('cancel', () => {
      if (!mountedRef.current) return;
      resetCallState();
      setStatus(STATUS.ready);
      onEndCall?.();
    });

    call.on('reject', () => {
      if (!mountedRef.current) return;
      resetCallState();
      setStatus(STATUS.ready);
      onEndCall?.();
    });

    call.on('error', (err) => {
      if (!mountedRef.current) return;
      const msg = err?.message || 'Call error';
      setError(msg);
      resetCallState();
      setStatus(STATUS.ready);
      onEndCall?.();
    });
  }, [onCallStart, onEndCall, resetCallState, stopTimer]);

  const setupDevice = useCallback(async () => {
    if (deviceRef.current) return deviceRef.current;

    setStatus(STATUS.loading);
    setError('');

    await ensureTwilioSdk();

    const tokenRes = await agentAPI.voiceToken(3600);
    if (!tokenRes?.token) {
      throw new Error('Unable to connect voice service');
    }

    const DeviceCtor = window.Twilio?.Device;
    if (!DeviceCtor) {
      throw new Error('Voice client not available');
    }

    const device = new DeviceCtor(tokenRes.token, {
      codecPreferences: ['opus', 'pcmu'],
      closeProtection: true,
      logLevel: 1,
    });

    device.on('registered', () => {
      if (!mountedRef.current) return;
      setStatus(STATUS.ready);
    });

    device.on('error', (err) => {
      if (!mountedRef.current) return;
      const msg = err?.message || 'Voice connection error';
      setError(msg);
      if (status !== STATUS.inCall && status !== STATUS.connecting) {
        setStatus(STATUS.ready);
      }
    });

    device.on('incoming', (call) => {
      if (!mountedRef.current) return;
      activeCallRef.current = call;
      bindCallEvents(call);
      call.accept();
    });

    await device.register();
    deviceRef.current = device;
    return device;
  }, [bindCallEvents, status]);

  const startCall = useCallback(async () => {
    try {
      setError('');
      setStatus(STATUS.loading);

      const device = await setupDevice();

      setStatus(STATUS.connecting);

      const params = {
        To: 'agent',
        callerName: String(displayName || '').trim(),
        callerEmail: String(prefilledEmail || '').trim(),
      };

      const call = await device.connect({ params });
      activeCallRef.current = call;
      bindCallEvents(call);
    } catch (err) {
      const msg = err?.message || 'Unable to start voice call';
      setError(msg);
      setStatus(STATUS.idle);
    }
  }, [bindCallEvents, displayName, prefilledEmail, setupDevice]);

  const endCall = useCallback(() => {
    try {
      if (activeCallRef.current) {
        activeCallRef.current.disconnect();
      } else if (deviceRef.current) {
        deviceRef.current.disconnectAll();
      }
    } catch (err) {
      const msg = err?.message || 'Unable to hang up';
      setError(msg);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopTimer();
      try {
        if (activeCallRef.current) activeCallRef.current.disconnect();
      } catch {
        // ignore cleanup errors
      }
      try {
        if (deviceRef.current) {
          deviceRef.current.disconnectAll();
          deviceRef.current.destroy();
        }
      } catch {
        // ignore cleanup errors
      }
      deviceRef.current = null;
      activeCallRef.current = null;
    };
  }, [stopTimer]);

  const mm = String(Math.floor(callSeconds / 60)).padStart(2, '0');
  const ss = String(callSeconds % 60).padStart(2, '0');

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: '#f8fafc',
      fontFamily: 'inherit',
    }}>
      <div style={{
        padding: '18px 20px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <div style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          boxShadow: '0 4px 16px rgba(34,197,94,0.4)',
        }}>
          PD
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>PowerDesk Voice Agent</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            {inCall ? `Live call ${mm}:${ss}` : 'Call support via browser voice'}
          </div>
        </div>
      </div>

      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 20px',
        textAlign: 'center',
      }}>
        <motion.div
          animate={pulse ? { scale: [1, 1.08, 1] } : { scale: 1 }}
          transition={{ duration: 1.2, repeat: pulse ? Infinity : 0, ease: 'easeInOut' }}
          style={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: inCall
              ? 'radial-gradient(circle at 30% 30%, #4ade80, #16a34a)'
              : 'radial-gradient(circle at 30% 30%, #64748b, #475569)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 42,
            marginBottom: 20,
            boxShadow: inCall
              ? '0 0 0 12px rgba(34,197,94,0.15), 0 8px 32px rgba(0,0,0,0.3)'
              : '0 8px 24px rgba(0,0,0,0.2)',
          }}
        >
          {status === STATUS.connecting ? '...' : 'CALL'}
        </motion.div>

        <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>
          {STATUS_LABEL[status] || STATUS_LABEL.idle}
        </div>

        {error && (
          <p style={{
            fontSize: 12,
            color: '#fecaca',
            margin: '0 0 14px',
            maxWidth: 320,
            background: 'rgba(239,68,68,0.12)',
            padding: '8px 12px',
            borderRadius: 8,
          }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={startCall}
            disabled={inCall || status === STATUS.loading}
            style={{
              border: 'none',
              borderRadius: 10,
              padding: '10px 16px',
              fontWeight: 700,
              cursor: inCall || status === STATUS.loading ? 'not-allowed' : 'pointer',
              background: '#22c55e',
              color: '#fff',
              opacity: inCall || status === STATUS.loading ? 0.55 : 1,
            }}
          >
            Start Call
          </button>
          <button
            type="button"
            onClick={endCall}
            disabled={!inCall}
            style={{
              border: 'none',
              borderRadius: 10,
              padding: '10px 16px',
              fontWeight: 700,
              cursor: !inCall ? 'not-allowed' : 'pointer',
              background: '#ef4444',
              color: '#fff',
              opacity: !inCall ? 0.55 : 1,
            }}
          >
            End Call
          </button>
        </div>
      </div>
    </div>
  );
}
